import _ from "lodash";
import { OnairEvent, type OnairOptions } from "./onair.ts";
import type { DaoMedia } from "../daos/dao-media.ts";
import {app, onair, print, throwError, use_mp3} from "./config.ts";
import { audioContext } from "../main.tsx";
import { Utils } from "./utils.ts";
import { StickyToast } from "./toast.ts";
import type { ApiResponse } from "./interfaces.ts";
import type { DaoApiMediaResource } from "../daos/dao-api-media-resource.ts";
import { Api } from "./api.ts";

type OnairAudioContextProps = "running" | "suspended" | "interrupted" | "closed" | "unknown";
type OnairPlayerStateProps = {
  isPlaying?: boolean;
  reason?: string;
  unknown?: boolean;
};

export class OnairPlayer {
  public id: string;
  private option: OnairOptions;

  public media: DaoMedia | null = null;
  private toast = StickyToast.shared;

  private readonly listenerOptions: AddEventListenerOptions = { passive: true };

  private readonly audioElement: HTMLAudioElement;
  private retry: number = 0;
  private retryTimer: number | null = null;
  private recovering: boolean = false;

  private autoPlayPending = false;

  private state: OnairPlayerStateProps = {
    isPlaying: false,
    reason: "init",
    unknown: false,
  };
  private lastTime: number | null = null;
  private hiddenSince: number = 0;

  private setState = (v: OnairPlayerStateProps, reason: string) => (this.state = { ...this.state, ...v, reason });

  // -------------------------------------------------------------------------------------------------
  // Watchdog (재생 멈춤/문제 자동 복원)
  // -------------------------------------------------------------------------------------------------
  private watchdogTimer: number | null = null;
  private watchdogLastProgressAt = 0; // 마지막 "진짜 진행" 확인 시각
  private watchdogLastTime = 0; // 마지막 확인한 currentTime
  private watchdogStuckCount = 0;
  private watchdogSoftRecovering = false;
  private watchdogHardRecovering = false;

  private readonly WD_INTERVAL_MS = 500; // 0.5초마다 체크
  private readonly WD_START_GRACE_MS = 3000; // start 직후 유예
  private readonly WD_NO_PROGRESS_MS = 5000; // 진행 없음 판단
  private readonly WD_MAX_STUCK = 2; // 연속 stuck 횟수
  private readonly WD_HIDDEN_RELAX_MS = 10000; // 백그라운드면 더 관대하게

  // ✅ 하드 복구 반복 횟수(같은 곡 기준)
  private hardFailCountByMedia = new Map<number, number>();

  // ✅ 하드 복구 연속 실패 백오프
  // 같은 곡이 재시작 직후 다시 실패하는 루프(예: 회선이 여러 개인 매장의 401 IP 불일치)에서
  // 서버에 수 초 간격으로 요청이 쏟아지지 않도록 재시작을 점점 늦춘다 (playing 시 초기화)
  private hardRecoverStreak = 0;
  private hardRecoverStreakMedia: number | null = null;
  private hardRecoverTimer: number | null = null;
  private readonly HARD_RECOVER_DELAYS = [0, 2000, 5000, 10000, 20000, 30000];

  // -------------------------------------------------------------------------------------------------
  // AudioContext interrupted/suspended 복구 (iOS WebKit 특히 필요)
  // -------------------------------------------------------------------------------------------------
  private interruptedRecoveryTimer: number | null = null;
  private interruptedRecoveryAttempt = 0;
  private interruptedGestureBound = false;
  private readonly INT_RECOVERY_DELAYS = [500, 1000, 2000, 4000, 8000, 8000, 15000];
  private readonly INT_RECOVERY_MAX_ATTEMPTS = 12;
  // 빠른 시도(INT_RECOVERY_MAX_ATTEMPTS)가 끝난 뒤에도 포기하지 않고 이 간격으로 계속 시도한다
  // (무인 매장 기기는 터치해 줄 사람이 없어 무음이 몇 시간씩 이어질 수 있다)
  private readonly INT_RECOVERY_IDLE_MS = 30000;
  // iOS 는 제스처 없는 AudioContext.resume() 이 응답 없이 대기할 수 있어 시간 제한을 둔다
  private readonly CTX_RESUME_TIMEOUT_MS = 2000;
  private watchdogCtxResuming = false;

  private startWatchdog() {
    this.stopWatchdog();

    this.watchdogLastProgressAt = Date.now();
    this.watchdogLastTime = this.audio?.currentTime ?? 0;
    this.watchdogStuckCount = 0;

    this.watchdogTimer = window.setInterval(() => {
      void this.watchdogTick();
    }, this.WD_INTERVAL_MS);
  }

  private stopWatchdog() {
    if (this.watchdogTimer != null) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
    this.watchdogSoftRecovering = false;
    this.watchdogHardRecovering = false;
  }

  private async watchdogTick(): Promise<void> {
    const a = this.audio;
    const m = this.media;
    if (!a || !m) return;

    // stop 상태 / src 없으면 패스
    if (!a.src) return;

    // recover 중이면 패스
    if (this.recovering) return;

    const now = Date.now();

    // start 직후 유예
    if (this.state.reason === "init" && now - this.watchdogLastProgressAt < this.WD_START_GRACE_MS) {
      return;
    }

    // hidden 상태면 더 관대
    const hidden = document.hidden || document.visibilityState !== "visible";
    const noProgressLimit = hidden ? this.WD_HIDDEN_RELAX_MS : this.WD_NO_PROGRESS_MS;

    // AudioContext가 suspended면 resume 시도 (결과를 기다리지 않음)
    // <audio> 재생은 AudioContext 에 연결돼 있지 않으므로, 여기서 빠져나가지 않고 멈춤 감지/복구를 계속한다
    // (예전에는 return 해서 컨텍스트가 suspended 인 동안 멈춘 재생이 복구되지 않았다)
    if ((audioContext.state as any) === "suspended" && !this.watchdogCtxResuming) {
      this.watchdogCtxResuming = true;
      void this.contextResumeWithTimeout().finally(() => {
        this.watchdogCtxResuming = false;
      });
    }

    // audio.error면 hard 복원
    if (a.error) {
      await this.watchdogHardRecover(`audio.error code=${a.error.code}`);
      return;
    }

    // "재생되어야 하는 상황" 추정
    const shouldBePlaying = this.autoPlayPending || this.state.isPlaying || (!a.paused && !a.ended);

    const cur = a.currentTime ?? 0;

    // 진행 여부 판단
    const progressed = cur > this.watchdogLastTime + 0.01;
    if (progressed) {
      this.watchdogLastTime = cur;
      this.watchdogLastProgressAt = now;
      this.watchdogStuckCount = 0;
      return;
    }

    // 진행 없음 시간
    const noProgressFor = now - this.watchdogLastProgressAt;

    // paused인데 재생돼야 하면 soft 복원
    if (shouldBePlaying && a.paused && !a.ended) {
      await this.watchdogSoftRecover("paused_but_should_play");
      return;
    }

    // readyState가 낮으면 로딩중일 수 있으나 너무 오래면 hard 복원
    if (a.readyState < a.HAVE_CURRENT_DATA) {
      if (noProgressFor > noProgressLimit) {
        await this.watchdogHardRecover(`readyState_low(${a.readyState})`);
      }
      return;
    }

    // 충분히 로드된 것 같은데도 진행이 없으면 stuck로 간주
    if (shouldBePlaying && noProgressFor > noProgressLimit) {
      this.watchdogStuckCount++;

      if (this.watchdogStuckCount >= this.WD_MAX_STUCK) {
        await this.watchdogHardRecover(`stuck(${this.watchdogStuckCount}) reason=${this.state.reason}`);
      } else {
        await this.watchdogSoftRecover(`stuck_soft(${this.watchdogStuckCount}) reason=${this.state.reason}`);
      }
    }
  }

  private async watchdogSoftRecover(tag: string) {
    if (this.watchdogSoftRecovering) return;
    this.watchdogSoftRecovering = true;

    try {
      print(`watchdog softRecover: ${tag}`, {
        id: this.id,
        reason: this.state.reason,
        currentTime: this.audio.currentTime,
        readyState: this.audio.readyState,
        networkState: this.audio.networkState,
        paused: this.audio.paused,
      });

      // 1) context resume
      try {
        await this.contextResume();
      } catch {}

      // 2) play 재시도
      await this.play(`watchdog_soft:${tag}`);

      // 재시도 이후 "진행" 유예용
      this.watchdogLastProgressAt = Date.now();
    } finally {
      this.watchdogSoftRecovering = false;
    }
  }

  // ✅ watchdog hardRecover는 한 곳(hardRecover)로 수렴
  private async watchdogHardRecover(tag: string) {
    if (this.watchdogHardRecovering) return;
    this.watchdogHardRecovering = true;

    try {
      await this.hardRecover(`watchdog:${tag}`);
    } catch (e) {
      print("watchdog hardRecover failed", e);
    } finally {
      this.watchdogHardRecovering = false;
    }
  }
  // -------------------------------------------------------------------------------------------------

  constructor(audioElement: HTMLAudioElement, option: OnairOptions) {
    this.option = option;
    this.id = audioElement.id;

    this.audioElement = audioElement;

    this.initVisibilityListener();
    this.initContextListener();
    this.initListener();
  }

  async uninit() {
    this.uninitVisibilityListener();
    this.uninitContextListener();
    this.uninitListener();

    await this.stop();
  }

  // ✅ 곡 바꾸기 전 항상 플레이어 리셋
  private resetAudioElementForNewSrc() {
    const a = this.audio;
    try {
      a.pause();
    } catch {}
    a.muted = true;
    a.volume = 0;
    try {
      a.currentTime = 0;
    } catch {}

    // ✅ 내부 상태까지 리셋
    try {
      a.removeAttribute("src");
    } catch {}
    try {
      (a as any).srcObject = null;
    } catch {}
    try {
      a.load();
    } catch {}
  }

  async start(media: DaoMedia): Promise<void> {
    try {
      // ✅ 곡 시작 전 오디오 엘리먼트 상태 리셋
      this.resetAudioElementForNewSrc();

      print("###### setmedia:: ", media);

      this.media = media;
      this.mediaSessionMetadata(true);

      let media_file: string = onair.getMediaFilename(
        media.media,
        media.m4a ?? null,
        media.m4a && !use_mp3 ? "m4a" : "mp3"
      );

      // 인터페이스 통신에 의한 저장된 음원 URL 인출
      let media_url = (await window.wpi?.media_uri?.(media_file)) as string;
      if (!_.isEmpty(media_url)) {
        media.url = media_url;
      }

      if (_.isEmpty(media.url)) {
        if (this.retry >= 3) {
          media.refresh = true;
        }

        const cancel: AbortController = new AbortController();
        const rs: ApiResponse<DaoApiMediaResource> | void = await Api.media_resource(
          media,
          onair.isAdvertise(this.media.type),
          cancel
        ).catch((e) => {
          print(e);
        });

        if (!rs || !rs.data) {
          if (this.retry < 3) {
            // stop() 이후에도 살아남아 옛 곡을 되살리지 않도록 타이머를 보관한다
            if (this.retryTimer != null) clearTimeout(this.retryTimer);
            this.retryTimer = window.setTimeout(() => {
              this.retryTimer = null;
              void this.start(media);
            }, 1000);
            this.retry++;
          } else {
            throwError("media load failed");
          }
          return;
        }

        const resource = rs.data as DaoApiMediaResource;
        this.audio.src = resource.url;

        // 인터페이스 통신에 의한 음원 저장
        media_file = onair.getMediaFilename(
          media.media,
          resource.bitrate ?? null,
          resource.file && !use_mp3 ? "m4a" : "mp3"
        );
        await window.wpi?.media_save?.(media_file, String(resource.length ?? 0), resource.url);
      } else {
        this.audio.src = media.url as string;
      }

      this.audio.muted = true;
      this.audio.volume = 0;
      this.audio.load();

      this.retry = 0;
      delete this.media.refresh;

      // 자동재생 요청
      await this.requestAutoPlay();

      // ✅ watchdog 시작
      this.startWatchdog();
    } catch (e) {
      onair.emit(OnairEvent.MEDIA_ERROR, this.media);
      try {
        await this.stop();
      } catch {}

      // 비상음원설정 - 파일 저장해서 사용하는 장치만 비상음원 설정이 가능하도록 한다
      try {
        if(!app.online && Utils.isMobileOrTableDevice()) onair.setEmergency();
      } catch (e) {
      }

      throw new Error(`[Player.start] media load error: ${String(e)}`);
    }
  }

  async stop(): Promise<void> {
    // ✅ watchdog 중지
    this.stopWatchdog();
    // ✅ interrupted 자동 복구 루프 정리
    this.clearInterruptedRecovery();
    // ✅ 미디어 재조회 재시도 타이머 정리(정지 후 옛 곡 되살아남 방지)
    if (this.retryTimer != null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    // ✅ 하드 복구 대기 타이머 정리(정지 후 옛 곡 되살아남 방지)
    if (this.hardRecoverTimer != null) {
      clearTimeout(this.hardRecoverTimer);
      this.hardRecoverTimer = null;
    }

    this.setState({ isPlaying: false, unknown: false }, "init");
    onair.emit(OnairEvent.MEDIA_END, this.media);
    this.media = null;

    if (this.audio) {
      try {
        this.audio.pause();
      } catch {}
      this.audio.muted = true;
      this.audio.volume = 0;
      try {
        this.audio.currentTime = 0;
      } catch {}

      try {
        this.audio.removeAttribute("src");
      } catch {}
      try {
        (this.audio as any).srcObject = null;
      } catch {}

      // ✅ 내부 상태 리셋 강제
      try {
        this.audio.load();
      } catch {}
    }
  }

  async play(tag: string) {
    try {
      await this.audio.play();

      // playing 이벤트에서 mute/volume을 다시 적용하고 있으니 여기서는 최소만
      this.audio.muted = false;
      this.setMute(this.option.mute);
      this.setVolume(this.option.volume);

      print(`player ${this.id}`, "play ok", tag, this.seekablePosition, this.option);
      return true;
    } catch (e) {
      print(`player ${this.id}`, "play fail", tag, (e as Error)?.name || e);
      return false;
    }
  }

  async requestAutoPlay() {
    this.autoPlayPending = true;
    if (this.media != null) {
      // this.audio.currentTime = this.offsets(this.media.start ?? null) ?? 0;
      const offset = this.offsets(this.media.start ?? null) ?? 0;
      const isAnnouncement = onair.isAdvertise(this.media.type);

      if (isAnnouncement && offset > 0 && offset <= 3) {
        // 멘트류: 초반 짤림 방지를 위해 처음부터 재생
        this.audio.currentTime = 0;
        // 처음부터 재생하는 만큼 종료시간을 offset초 연장 (ticker가 조기 종료하는 것 방지)
        const endFormat = this.media.end.length === 19 ? 'yyyy-MM-dd HH:mm:ss' : 'HH:mm:ss';
        const endDate = Utils.toDate(this.media.end, endFormat, onair.getDate());
        const newEnd = new Date(endDate.getTime() + Math.ceil(offset) * 1000);
        this.media.end = Utils.toDateString(newEnd, endFormat);
      } else {
        this.audio.currentTime = Math.max(0, offset);
      }
    }
    await this.tryAutoPlay();
  }

  private tryAutoPlay = async () => {
    const a = this.audio;
    if (!this.autoPlayPending || !a) return;

    // 에러 상태면 포기
    if (a.error) {
      this.autoPlayPending = false;
      return;
    }

    // 2(HAVE_CURRENT_DATA)면 재생 시도 가능
    if (a.readyState >= a.HAVE_CURRENT_DATA) {
      this.autoPlayPending = false;
      try {
        await this.play("playAuto");
      } catch {}
    }
  };

  async resume() {
    if (!this.state.isPlaying) {
      if (this.state.reason === "audiocontext_suspended") await this.contextResume();
      await this.requestAutoPlay();
    }
  }

  status() {
    return Object.assign(
      {
        id: this.id,
        media: this.media ? this.media.media : 0,
        current: this.currentTime,
      },
      this.state
    );
  }

  get audio() {
    return this.audioElement;
  }

  get currentTime(): number {
    return this.audio?.currentTime ?? 0;
  }

  get totalTime(): number {
    return this.media?.duration ?? 0;
  }

  setVolume(volume: number, update: boolean = true) {
    if (update) this.option.volume = volume;
    if (!this.option.mute) {
      this.audio.volume = volume;
    }
  }

  setMute(muted: boolean, update: boolean = true) {
    if (update) this.option.mute = muted;
    this.audio.muted = this.option.mute;
  }

  setOption(option: OnairOptions) {
    this.option = { ...this.option, ...option };
    this.setMute(this.option.mute, false);
    this.setVolume(this.option.volume, false);
  }

  get seekablePosition() {
    const range: TimeRanges | null = this.audio?.seekable ?? null;
    return range && range.length ? range.end(range.length - 1) : 0;
  }

  get isPlaying(): { value: boolean; confidence: string } {
    if (this.hiddenSince && Date.now() - this.hiddenSince > 2000 && !this.audio.paused && !this.audio.ended) {
      return { value: this.state.isPlaying as boolean, confidence: this.state.unknown ? "low" : "mid" };
    }
    return { value: this.state.isPlaying as boolean, confidence: this.state.unknown ? "mid" : "high" };
  }

  // ✅ 캐시 삭제 판단은 hardRecover 한 곳에서만
  private maybeDeleteLocalCache(reason: string) {
    const m = this.media;
    if (!m) return;

    const url = m.url ?? "";
    const isLocal = Utils.isLocalMedia(url);
    if (!isLocal) return;

    const key = m.media;
    const prev = this.hardFailCountByMedia.get(key) ?? 0;
    const next = prev + 1;
    this.hardFailCountByMedia.set(key, next);

    const code = this.audio?.error?.code;

    const isBadCodec =
      code === MediaError.MEDIA_ERR_DECODE || code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED;

    // ✅ 조건 단순화: (코덱계열) OR (같은 곡 hardRecover 2회 이상)
    const shouldDelete = isBadCodec || next >= 2;
    if (!shouldDelete) return;

    const mediafile = onair.getMediaFilename(
      m.media,
      m.m4a ?? null,
      m.m4a && !use_mp3 ? "m4a" : "mp3"
    );

    try {
      window.wpi?.media_delete?.(mediafile);
      print("media_delete (hardRecover)", { mediafile, reason, code, next, url });
    } catch (e) {
      print("media_delete failed", e);
    }

    // 로컬 URL 제거 → 다음 start에서 Api.media_resource 타게 유도
    delete m.url;

    // 삭제했으면 카운트 리셋
    this.hardFailCountByMedia.set(key, 0);
  }

  // ✅ 하드 복구를 한 곳으로 수렴
  private async hardRecover(reason: string) {
    if (this.recovering) return;
    const backup = this.media;
    if (!backup) return;

    print(`hardRecover: ${reason}`, {
      id: this.id,
      media: backup.media,
      url: backup.url,
      code: this.audio?.error?.code,
      readyState: this.audio?.readyState,
      networkState: this.audio?.networkState,
    });

    // ✅ 삭제는 recover에서만 하도록 위임
    await this.recover(new Event("hardRecover"), { allowDeleteCache: true, reason });

    // 같은 곡 연속 실패 횟수에 따라 재시작을 늦춘다
    if (this.hardRecoverStreakMedia !== backup.media) {
      this.hardRecoverStreakMedia = backup.media;
      this.hardRecoverStreak = 0;
    }
    const delay =
      this.HARD_RECOVER_DELAYS[Math.min(this.hardRecoverStreak, this.HARD_RECOVER_DELAYS.length - 1)];
    this.hardRecoverStreak++;

    // recover()가 stop()까지 하므로 백업으로 재시작
    if (delay <= 0) {
      await this.start(backup);
      return;
    }

    print(`hardRecover: restart in ${delay}ms`, { id: this.id, media: backup.media, streak: this.hardRecoverStreak });
    this.hardRecoverTimer = window.setTimeout(() => {
      this.hardRecoverTimer = null;
      // 대기 중 이 플레이어에 다른 곡이 시작됐거나 현재 방송 곡이 바뀌었으면 옛 곡을 되살리지 않는다
      if (this.media != null || (onair.media != null && onair.media !== backup)) return;
      this.start(backup).catch((e) => print("hardRecover restart failed", e));
    }, delay);
  }

  async recover(
    event?: Event,
    opts?: { allowDeleteCache?: boolean; reason?: string }
  ): Promise<void> {
    if (this.recovering) {
      print("recover skipped (already recovering)");
      return;
    }
    this.recovering = true;

    try {
      this.mediaSessionMetadata();
      print("recover", { url: this.media?.url, media: this.media, event: event?.type, opts });

      // ✅ 여기서만 캐시 삭제 (옵션 true일 때만)
      if (opts?.allowDeleteCache) {
        this.maybeDeleteLocalCache(opts?.reason ?? event?.type ?? "recover");
      }

      try {
        onair.emit(OnairEvent.MEDIA_ERROR, this.media, event ?? new Event("error"));
      } catch {}
    } finally {
      try {
        await this.stop();
      } catch {}
      this.recovering = false;
    }
  }

  offsets(strDate: string | null): number {
    try {
      if (strDate === null) return 0;
      const start: Date = Utils.toDate(
        strDate,
        strDate.length === 19 ? "yyyy-MM-dd HH:mm:ss" : "HH:mm:ss",
        onair.getDate()
      );
      return Number(((onair.millisecond - start.getTime()) / 1000).toFixed(3));
    } catch {}
    return 0;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /// 미디어 세션
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  mediaSessionMetadata(interfaceCall: boolean = false) {
    if (this.media) {
      const title: string = this.media.title ?? "unknown title";
      const artist: string = this.media.artist_name ?? "unknown artist";
      const album: string = this.media.album_title ?? "unknown album";
      const image: string = `${window.location.origin}${
        !_.isEmpty(this.media.album_image) ? this.media.album_image : "/default-img.png"
      }`;

      if ("mediaSession" in window.navigator) {
        window.navigator.mediaSession.metadata = new MediaMetadata({
          title,
          artist,
          album,
          artwork: [{ src: image, type: "image/jpg" }],
        });

        window.navigator.mediaSession.setActionHandler("play", function () {});
        window.navigator.mediaSession.setActionHandler("pause", function () {});
        window.navigator.mediaSession.setActionHandler("previoustrack", null);
        window.navigator.mediaSession.setActionHandler("nexttrack", null);
        window.navigator.mediaSession.setActionHandler("seekbackward", null);
        window.navigator.mediaSession.setActionHandler("seekforward", null);
        window.navigator.mediaSession.setActionHandler("seekto", null);
        window.navigator.mediaSession.setActionHandler("stop", null);

        this.mediaSessionPosition(1, this.media.duration);
      }

      if (interfaceCall) {
        window.wpi?.media_session?.(title, artist, album, image);
      }
    }
  }

  mediaSessionPosition(position: number, duration: number) {
    if ("mediaSession" in window.navigator) {
      window.navigator.mediaSession.setPositionState({
        duration,
        position,
        playbackRate: 1.0,
      });
      window.navigator.mediaSession.playbackState = "playing";
    }
  }
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /// 문서 노출 변경 이벤트 리스터
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  private initVisibilityListener() {
    document.addEventListener("visibilitychange", this.visibilityHandler, this.listenerOptions);
  }

  private uninitVisibilityListener() {
    document.removeEventListener("visibilitychange", this.visibilityHandler, this.listenerOptions);
  }

  private visibilityHandler = (_event: Event) => {
    this.hiddenSince = document.hidden || document.visibilityState !== "visible" ? Date.now() : 0;
    this.mediaSessionMetadata();
  };
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /// 오디오 컨텍스트 리스너
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  async contextResume() {
    await audioContext.resume();
  }

  /** resume 이 응답 없이 대기해도 CTX_RESUME_TIMEOUT_MS 후에는 반환한다 */
  private async contextResumeWithTimeout(): Promise<void> {
    let timer: number | null = null;
    try {
      await Promise.race([
        this.contextResume(),
        new Promise<void>((resolve) => {
          timer = window.setTimeout(resolve, this.CTX_RESUME_TIMEOUT_MS);
        }),
      ]);
    } catch {
    } finally {
      if (timer != null) clearTimeout(timer);
    }
  }

  private initContextListener() {
    audioContext.addEventListener("statechange", this.audioContextHandler, true);
  }

  private uninitContextListener() {
    audioContext.removeEventListener("statechange", this.audioContextHandler, true);
  }

  private audioContextHandler = async (_event: Event): Promise<void> => {
    print(`player ${this.id} - ${this.isPlaying}`, "statechange", audioContext.state, _event);
    const state: OnairAudioContextProps = audioContext.state as OnairAudioContextProps;

    if (state === "suspended") {
      this.setState({ isPlaying: false, unknown: true }, "audiocontext_suspended");
      this.toast.show("오디오 컨텍스트가 중단되었습니다(절전/백그라운드 영향 가능).", "warn", {
        audioContext: audioContext.state,
      });
      this.startInterruptedRecovery();
    } else if (state === "interrupted") {
      this.setState({ isPlaying: false, unknown: true }, "audiocontext_interrupted");
      this.toast.show("오디오 컨텍스트가 인터럽트되었습니다. 자동 복구 시도 중...", "warn", {
        audioContext: audioContext.state,
      });
      this.startInterruptedRecovery();
    } else if (state === "running") {
      this.clearInterruptedRecovery();
      if (!this.audio.paused && !this.audio.ended) {
        this.setState({ isPlaying: true, unknown: false }, "audiocontext_running");
      } else if (this.media && this.audio.paused && !this.audio.ended) {
        // 인터럽트/서스펜드 후 복귀했지만 audio가 paused 상태 → 재생 재개
        const ok = await this.play("audiocontext_running_resume");
        if (ok) this.setState({ isPlaying: true, unknown: false }, "audiocontext_running");
      }
    }
  };

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /// interrupted / suspended 자동 복구
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  private startInterruptedRecovery() {
    if (this.interruptedRecoveryTimer != null) return; // 이미 진행 중
    this.interruptedRecoveryAttempt = 0;
    this.bindInterruptedGesture();
    this.scheduleInterruptedRecovery();
  }

  private clearInterruptedRecovery() {
    if (this.interruptedRecoveryTimer != null) {
      clearTimeout(this.interruptedRecoveryTimer);
      this.interruptedRecoveryTimer = null;
    }
    this.interruptedRecoveryAttempt = 0;
    this.unbindInterruptedGesture();
  }

  private scheduleInterruptedRecovery() {
    const idx = Math.min(this.interruptedRecoveryAttempt, this.INT_RECOVERY_DELAYS.length - 1);
    const delay =
      this.interruptedRecoveryAttempt >= this.INT_RECOVERY_MAX_ATTEMPTS
        ? this.INT_RECOVERY_IDLE_MS
        : this.INT_RECOVERY_DELAYS[idx];
    this.interruptedRecoveryTimer = window.setTimeout(() => {
      this.interruptedRecoveryTimer = null;
      void this.tryRecoverInterrupted(`auto#${this.interruptedRecoveryAttempt}`);
    }, delay);
  }

  private async tryRecoverInterrupted(tag: string) {
    // 이미 정상 재생 중이면 종료
    if (audioContext.state === "running" && !this.audio.paused && !this.audio.ended) {
      this.clearInterruptedRecovery();
      return;
    }

    // 백그라운드 상태에서는 시도해도 거의 실패 → 다음 visibility 이벤트까지 대기
    const hidden = document.hidden || document.visibilityState !== "visible";

    this.interruptedRecoveryAttempt++;
    print(
      `player ${this.id} interrupted recover try`,
      tag,
      "ctx=",
      audioContext.state,
      "hidden=",
      hidden
    );

    if (!hidden) {
      await this.contextResumeWithTimeout();

      // <audio> 는 AudioContext 에 연결돼 있지 않으므로 컨텍스트가 running 이 아니어도 재생을 시도한다
      if (this.media && this.audio.paused && !this.audio.ended) {
        const ok = await this.play(`interrupted_recover:${tag}`);
        if (ok) {
          this.clearInterruptedRecovery();
          return;
        }
      } else if (!this.media || (audioContext.state as OnairAudioContextProps) === "running") {
        // 복구할 곡이 없거나, 이미 재생 중이고 컨텍스트도 정상
        this.clearInterruptedRecovery();
        return;
      }
    }

    if (this.interruptedRecoveryAttempt === this.INT_RECOVERY_MAX_ATTEMPTS) {
      print(
        `player ${this.id} interrupted recovery slow mode`,
        `attempts=${this.interruptedRecoveryAttempt}`,
        `next every ${this.INT_RECOVERY_IDLE_MS}ms`
      );
      // 사용자 조작을 안내하는 메시지라 조금 더 오래 노출한다
      this.toast.show(
        "재생이 중단되었습니다. 자동으로 계속 다시 시도하며, 화면을 터치하면 바로 시작합니다.",
        "warn",
        { audioContext: audioContext.state },
        15000
      );
    }
    // 빠른 시도가 끝나도 포기하지 않고 INT_RECOVERY_IDLE_MS 간격으로 계속 시도 (제스처 폴백도 유지)
    this.bindInterruptedGesture();
    this.scheduleInterruptedRecovery();
  }

  private bindInterruptedGesture() {
    if (this.interruptedGestureBound) return;
    this.interruptedGestureBound = true;
    document.addEventListener("touchend", this.interruptedGestureHandler, { capture: true, once: true });
    document.addEventListener("click", this.interruptedGestureHandler, { capture: true, once: true });
    window.addEventListener("focus", this.interruptedGestureHandler, { once: true });
    document.addEventListener("visibilitychange", this.interruptedVisibilityHandler, this.listenerOptions);
  }

  private unbindInterruptedGesture() {
    if (!this.interruptedGestureBound) return;
    this.interruptedGestureBound = false;
    document.removeEventListener("touchend", this.interruptedGestureHandler, true);
    document.removeEventListener("click", this.interruptedGestureHandler, true);
    window.removeEventListener("focus", this.interruptedGestureHandler);
    document.removeEventListener("visibilitychange", this.interruptedVisibilityHandler, this.listenerOptions);
  }

  private interruptedGestureHandler = (_e: Event) => {
    this.unbindInterruptedGesture();
    void this.tryRecoverInterrupted("user_gesture");
  };

  private interruptedVisibilityHandler = (_e: Event) => {
    // 포그라운드로 돌아왔을 때 즉시 복구 시도
    if (!document.hidden && document.visibilityState === "visible") {
      void this.tryRecoverInterrupted("visibility_visible");
    }
  };
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /// 오디오 리스너
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  private initListener() {
    this.audio?.addEventListener("play", this.audioPlayHandler, this.listenerOptions);
    this.audio?.addEventListener("playing", this.audioPlayingHandler, this.listenerOptions);
    this.audio?.addEventListener("pause", this.audioPauseHandler, this.listenerOptions);
    this.audio?.addEventListener("ended", this.audioEndedHandler, this.listenerOptions);
    this.audio?.addEventListener("waiting", this.audioWaitingHandler, this.listenerOptions);
    this.audio?.addEventListener("stalled", this.audioStalledHandler, this.listenerOptions);

    this.audio?.addEventListener("loadstart", this.audioLoadstartHandler, this.listenerOptions);
    this.audio?.addEventListener("progress", this.audioProgressHandler, this.listenerOptions);
    this.audio?.addEventListener("suspend", this.audioSuspendHandler, this.listenerOptions);
    this.audio?.addEventListener("abort", this.audioAbortHandler, this.listenerOptions);
    this.audio?.addEventListener("error", this.audioErrorHandler, this.listenerOptions);
    this.audio?.addEventListener("emptied", this.audioEmptiedHandler, this.listenerOptions);
    this.audio?.addEventListener("loadedmetadata", this.audioLoadedMetadataHandler, this.listenerOptions);
    this.audio?.addEventListener("loadeddata", this.audioLoadedDataHandler, this.listenerOptions);
    this.audio?.addEventListener("canplay", this.audioCanplayHandler, this.listenerOptions);
    this.audio?.addEventListener("canplaythrough", this.audioCanplayThroughHandler, this.listenerOptions);
    this.audio?.addEventListener("seeking", this.audioSeekingHandler, this.listenerOptions);
    this.audio?.addEventListener("seeked", this.audioSeekedHandler, this.listenerOptions);
    this.audio?.addEventListener("timeupdate", this.audioTimeUpdateHandler, this.listenerOptions);
    this.audio?.addEventListener("ratechange", this.audioRateChangeHandler, this.listenerOptions);
    this.audio?.addEventListener("durationchange", this.audioDurationChangeHandler, this.listenerOptions);
  }

  private uninitListener() {
    this.audio?.removeEventListener("play", this.audioPlayHandler, this.listenerOptions);
    this.audio?.removeEventListener("playing", this.audioPlayingHandler, this.listenerOptions);
    this.audio?.removeEventListener("pause", this.audioPauseHandler, this.listenerOptions);
    this.audio?.removeEventListener("ended", this.audioEndedHandler, this.listenerOptions);
    this.audio?.removeEventListener("waiting", this.audioWaitingHandler, this.listenerOptions);
    this.audio?.removeEventListener("stalled", this.audioStalledHandler, this.listenerOptions);

    this.audio?.removeEventListener("loadstart", this.audioLoadstartHandler, this.listenerOptions);
    this.audio?.removeEventListener("progress", this.audioProgressHandler, this.listenerOptions);
    this.audio?.removeEventListener("suspend", this.audioSuspendHandler, this.listenerOptions);
    this.audio?.removeEventListener("abort", this.audioAbortHandler, this.listenerOptions);
    this.audio?.removeEventListener("error", this.audioErrorHandler, this.listenerOptions);
    this.audio?.removeEventListener("emptied", this.audioEmptiedHandler, this.listenerOptions);
    this.audio?.removeEventListener("loadedmetadata", this.audioLoadedMetadataHandler, this.listenerOptions);
    this.audio?.removeEventListener("loadeddata", this.audioLoadedDataHandler, this.listenerOptions);
    this.audio?.removeEventListener("canplay", this.audioCanplayHandler, this.listenerOptions);
    this.audio?.removeEventListener("canplaythrough", this.audioCanplayThroughHandler, this.listenerOptions);
    this.audio?.removeEventListener("seeking", this.audioSeekingHandler, this.listenerOptions);
    this.audio?.removeEventListener("seeked", this.audioSeekedHandler, this.listenerOptions);
    this.audio?.removeEventListener("timeupdate", this.audioTimeUpdateHandler, this.listenerOptions);
    this.audio?.removeEventListener("ratechange", this.audioRateChangeHandler, this.listenerOptions);
    this.audio?.removeEventListener("durationchange", this.audioDurationChangeHandler, this.listenerOptions);
  }

  private audioPlayHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioPlayingHandler = (e: Event) => {
    this.autoPlayPending = false;
    // 정상 재생되면 하드 복구 백오프 초기화
    this.hardRecoverStreak = 0;

    this.setState({ isPlaying: true, unknown: false }, e.type);

    this.setMute(this.option.mute);
    this.setVolume(this.option.volume);

    onair.emit(OnairEvent.MEDIA, this.media);
    this.mediaSessionMetadata();
  };

  private audioPauseHandler = (e: Event) => {
    this.setState({ isPlaying: false, unknown: false }, e.type);
    this.mediaSessionMetadata();
  };

  private audioEndedHandler = (e: Event) => {
    this.autoPlayPending = false;

    this.setState({ isPlaying: false, unknown: false }, e.type);
    this.mediaSessionMetadata();
  };

  private audioWaitingHandler = (e: Event) => {
    this.setState({ isPlaying: false, unknown: true }, e.type);
    this.mediaSessionMetadata();
  };

  private audioStalledHandler = (e: Event) => {
    this.setState({ isPlaying: false, unknown: true }, e.type);
    this.mediaSessionMetadata();
  };

  // ✅ onerror는 "로깅 + hardRecover 호출"만
  private audioErrorHandler = (e: Event) => {
    this.autoPlayPending = false;

    const a = (e.currentTarget as HTMLMediaElement | null) ?? this.audio;
    const code = a?.error?.code;

    print("audio error", {
      id: this.id,
      code,
      url: this.media?.url,
      readyState: a?.readyState,
      networkState: a?.networkState,
    });

    if (code === MediaError.MEDIA_ERR_ABORTED) return;

    void this.hardRecover(`audio_error code=${code ?? "?"}`);
  };

  private audioLoadstartHandler = async (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioProgressHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioSuspendHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioAbortHandler = (_: Event) => {
    this.autoPlayPending = false;
    this.mediaSessionMetadata();
  };

  private audioEmptiedHandler = (_: Event) => {
    // empty
  };

  private audioLoadedMetadataHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioLoadedDataHandler = async (_: Event) => {
    this.mediaSessionMetadata();
    await this.tryAutoPlay();
  };

  private audioCanplayHandler = async (_: Event) => {
    this.mediaSessionMetadata();
    await this.tryAutoPlay();
  };

  private audioCanplayThroughHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioSeekingHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioSeekedHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioTimeUpdateHandler = (_: Event) => {
    onair.emit(OnairEvent.DURATION, this.currentTime, this.totalTime, this.media);

    // ✅ watchdog 진행 신호 갱신
    this.watchdogLastProgressAt = Date.now();

    if (this.audio.playbackRate === 0) return this.setState({ isPlaying: false }, "rate_0");

    if (this.lastTime != null) {
      const moved = this.currentTime > this.lastTime + 0.01;
      if (moved) this.setState({ isPlaying: true, unknown: false }, "timeupdate");
    }
    this.lastTime = this.currentTime;

    this.mediaSessionPosition(this.currentTime, this.media?.duration ?? 0);
  };

  private audioRateChangeHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  private audioDurationChangeHandler = (_: Event) => {
    this.mediaSessionMetadata();
  };

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
}
